import React, { useMemo, useState } from 'react';
import { useForm, SubmitHandler, FieldValues } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { CountrySetting, Partner, PartnerCountry } from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { useApiClient, useFetch, useFetchImmutable } from '@marageti/z4-lib';
import { useSnackbar } from 'notistack';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import CountriesView from './countries-view';
import { EnchancedSetting, EnchancedPartnerCountry } from './types';

const getDefault = (array: any[] = []) => array?.find((cs: CountrySetting) => cs.country === 'Default') || {};

const getCountrySetting = (countries: PartnerCountry[] | undefined, code: string | undefined): PartnerCountry => {
  if (!countries || !code) return {} as PartnerCountry;
  return countries.filter((ele) => ele.country === code)[0];
};

const CountriesContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { partnerId } = useParams();
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const { data: countrySettings, error: countrySettingsError } = useFetchImmutable<CountrySetting[]>(apiClient.countrySettingsClient.getCountrySettings, [], true);
  const { data: partner, mutate: mutatePartner } = useFetch<Partner>(apiClient.partnersClient.getPartner, [partnerId], !!partnerId);
  const [submissionError, setSubmissionError] = useState<ApiError>();

  if (countrySettingsError?.code === 401) navigate('/logout');

  const { setValue, reset } = useForm();

  const [showModal, setShowModal] = useState(false);
  const [editorState, setEditorState] = useState<PartnerCountry>();
  const [settingToDelete, setSettingToDelete] = useState<EnchancedSetting>();

  const globalCountryDefault = useMemo(() => getDefault(countrySettings), [countrySettings]);

  const primaryCountries = useMemo(() => {
    if (countrySettings && partner && countryCodes) {
      return partner.primaryCountries
        .map((c: PartnerCountry): EnchancedPartnerCountry => {
          if (c.minBudgetPPPD) return { ...c, displayName: countryCodes[c.country] };
          return {
            ...c,
            displayName: countryCodes[c.country],
            minBudgetPPPD: countrySettings?.find((cs: CountrySetting) => cs.isoCode2 === c.country)?.minBudgetPPPD || globalCountryDefault.minBudgetPPPD,
          };
        })
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
    }
    return partner?.primaryCountries.map((c: PartnerCountry): EnchancedPartnerCountry => ({ ...c, displayName: '' }));
  }, [partner, countrySettings, globalCountryDefault, countryCodes]);

  const secondaryCountries = useMemo(() => {
    if (countrySettings && partner && countryCodes) {
      return partner.secondaryCountries
        .map((c: PartnerCountry): EnchancedPartnerCountry => {
          if (c.minBudgetPPPD) return { ...c, displayName: countryCodes[c.country] };
          return {
            ...c,
            displayName: countryCodes[c.country],
            minBudgetPPPD: countrySettings?.find((cs: CountrySetting) => cs.isoCode2 === c.country)?.minBudgetPPPD || globalCountryDefault.minBudgetPPPD,
          };
        })
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
    }
    return partner?.secondaryCountries.map((c: PartnerCountry): EnchancedPartnerCountry => ({ ...c, displayName: '' }));
  }, [partner, countrySettings, globalCountryDefault, countryCodes]);

  const excludedCountries: CountryCodeMap | any = useMemo(() => {
    if (!countryCodes) return {};
    if (!partner || !partner.countries || !partner.countries.length) return {};
    return partner.countries.reduce((acc: CountryCodeMap, cur: PartnerCountry) => {
      if (!acc[cur.country]) acc[cur.country] = countryCodes[cur.country];
      return acc;
    }, {});
  }, [countryCodes, partner]);

  const setFormValues = (countrySetting: PartnerCountry) => {
    setValue('country', countrySetting.country || '');
    setValue('minBudgetPPPD', countrySetting.minBudgetPPPD);
  };

  const onCountryChange = (country: any) => {
    setValue('country', country);
  };

  const onClickEdit = (countrySetting: EnchancedSetting) => {
    const settingToEdit = getCountrySetting(partner?.countries, countrySetting.country);
    setEditorState({
      ...settingToEdit,
      minBudgetPPPD: countrySetting.minBudgetPPPD,
    });
    setFormValues({
      ...settingToEdit,
      minBudgetPPPD: countrySetting.minBudgetPPPD,
    });
    setShowModal(true);
  };

  const onClickDelete = (countrySetting: EnchancedSetting) => {
    setSettingToDelete(countrySetting);
  };

  const onConfirmDelete = async () => {
    if (settingToDelete?.country && partnerId) {
      try {
        await apiClient.partnersClient.deletePartnerCountry(
          partnerId,
          settingToDelete.country,
        );
        mutatePartner();
        enqueueSnackbar('Country setting deleted.', { variant: 'success' });
      } catch (error) {
        setSubmissionError(error as ApiError);
        enqueueSnackbar('Error to deleting country setting.', { variant: 'default' });
      } finally {
        setSettingToDelete(undefined);
      }
    }
  };

  const onCancelDelete = () => setSettingToDelete(undefined);

  const closeModal = () => {
    setShowModal(false);
    setEditorState(undefined);
    const defaultSetting = globalCountryDefault || {};
    reset({
      country: '',
      minBudgetPPPD: defaultSetting.minBudgetPPPD,
    });
  };

  const onClickCreate = () => {
    setShowModal(true);
    setEditorState(undefined);
    const defaultSetting = globalCountryDefault || {};
    reset({
      country: '',
      minBudgetPPPD: defaultSetting.minBudgetPPPD,
    });
  };

  const editPartnerCountry = async (country: PartnerCountry) => {
    if (!partnerId) return;
    try {
      await apiClient.partnersClient.putPartnerCountry(
        partnerId,
        country.country,
        country,
      );
      enqueueSnackbar('Country setting saved.', { variant: 'success' });
      mutatePartner();
    } catch (error) {
      setSubmissionError(error as ApiError);
      enqueueSnackbar('Error saving country setting.', { variant: 'default' });
    }
  };

  const addPartnerCountry = async (country: PartnerCountry) => {
    if (!partnerId) return;
    try {
      await apiClient.partnersClient.postPartnerCountry(
        partnerId,
        country,
      );
      enqueueSnackbar('Country setting added.', { variant: 'success' });
      mutatePartner();
    } catch (error) {
      setSubmissionError(error as ApiError);
      enqueueSnackbar('Error adding country setting.', { variant: 'default' });
    }
  };

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    const merged: PartnerCountry = {
      minBudgetPPPD: data.minBudgetPPPD,
      primary: data.primary,
      country: data.country.value || data.country,
    };
    const exists = partner?.countries.find((c: PartnerCountry) => c.country === merged.country);
    if (exists) editPartnerCountry(merged);
    else addPartnerCountry(merged);
  };

  return (
    <CountriesView
      countryDefault={globalCountryDefault}
      editorState={editorState}
      excludedCountries={excludedCountries}
      onCancelDelete={onCancelDelete}
      onClickCreate={onClickCreate}
      onClickDelete={onClickDelete}
      onClickEdit={onClickEdit}
      onClose={closeModal}
      onConfirmDelete={onConfirmDelete}
      onCountryChange={onCountryChange}
      onSubmit={onSubmit}
      primaryCountries={primaryCountries}
      secondaryCountries={secondaryCountries}
      settingToDelete={settingToDelete}
      showModal={showModal}
      submissionError={submissionError}
    />
  );
};

export default CountriesContainer;
